import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { gapi } from "gapi-script";
import apis from "../utils/apis";
import { handleApiError } from "../store/actions/utils/utils";
import { useAppDispatch } from "../store";

interface GoogleDriveContextProps {
  accessToken: string | null;
  isSignedIn: boolean;
  initClient: () => Promise<void>;
  signIn: () => Promise<void>;
  signOut: () => void;
  listFiles: () => Promise<any>;
  downloadFile: (fileId: string, fileName: string) => Promise<void>;
  handleFileSelection: (fileId: string) => Promise<void>;
  initTokenClient: () => void;
  handleOpenPicker: (onFilePicked: any) => void;
  pickerLoaded: boolean;
  getBackendToken: () => Promise<void>;   
}

const defaultContext: GoogleDriveContextProps = {
  accessToken: null,
  isSignedIn: false,
  initClient: async () => { },
  signIn: async () => { },
  signOut: () => { },
  listFiles: async () => [],
  downloadFile: async () => { },
  handleFileSelection: async () => { },
  initTokenClient: async () => { },
  handleOpenPicker: async (onFilePicked: () => void) => { },
  pickerLoaded: false,
  getBackendToken: async () => { },
};

export let tokenClient: any;
export let accessToken: string | null = null;

const GoogleDriveContext = createContext<GoogleDriveContextProps>(defaultContext);

export const GoogleDriveProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const API_KEY =
    process.env.REACT_APP_GOOGLE_API_KEY || "";

  const [isSignedIn, setIsSignedIn] = useState(false);
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const dispatch = useAppDispatch()

  const initClient = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      gapi.load("client", async () => {
        try {
          await gapi.client.init({
            apiKey: API_KEY,
            discoveryDocs: [
              "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
            ],
          });
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  }, [API_KEY]);


  const signIn = async () => {
    return new Promise<void>((resolve, reject) => {
      if (accessToken) {
        resolve();
      } else {
        tokenClient.callback = (tokenResponse: any) => {
          if (tokenResponse.error) {
            reject(tokenResponse.error);
            return;
          }
          accessToken = tokenResponse.access_token;
          gapi.client.setToken({ access_token: accessToken });
          resolve();
        };
        tokenClient.requestAccessToken({ prompt: 'consent' });
      }
    });
  };

  const getBackendToken = async () => {
    try {
      if (accessToken) return

      const response = await apis.get<{ access_token: string }>(
        "/integrations/auth/tokens/gdrive",
        { withCredentials: true }
      )

      if (response.status === 200 && response.data.access_token) {
        accessToken = response.data.access_token
        gapi.client.setToken({ access_token: accessToken })
      } else {
        handleApiError(response, dispatch)
      }
    } catch (err: any) {
    }

  }

  // Sign Out
  const signOut = () => {
    if (accessToken) {
      window.google.accounts.oauth2.revoke(accessToken, () => {
        setIsSignedIn(false);
        gapi.client.setToken(null);
      });
    }
  };

  const listFiles = async () => {
    return gapi.client.drive.files.list();
  };

  const downloadFile = async (fileId: string, fileName: string) => {
    try {
      if (!accessToken) await signIn();

      const response = await gapi.client.request({
        path: `/drive/v3/files/${fileId}`,
        method: "GET",
        params: { alt: "media" },
        responseType: "blob",
      });

      const blob = new Blob([response.body], {
        type: response.headers["Content-Type"],
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Silently handle download errors
    }
  };

  const handleFileSelection = async (fileId: string) => {
    try {
      await gapi.client.drive.files.get({
        fileId,
        alt: "media",
      });
    } catch (error) {
      // Silently handle file selection errors
    }
  };

  const handleOpenPicker = async (onFilePicked: any) => {
    if (!pickerLoaded) {
      return;
    }
    await getBackendToken();

    if (!accessToken) {
      window.location.href = '/settings?tab=integrations'
    } else {
      openPicker(accessToken, onFilePicked);
    }
  };


  const openPicker = (oauthToken: string, onFilePicked: any) => {
    gapi.load("picker", () => {
      const docsView = new google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setMimeTypes(
          "text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        )
        .setMode(google.picker.DocsViewMode.LIST)
      const picker = new google.picker.PickerBuilder()
        .addView(docsView)
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(oauthToken)
        .setDeveloperKey(API_KEY)
        .setCallback((data: any) => pickerCallback(data, onFilePicked))
        .build();


      picker.setVisible(true);
    });
  };

  const fetchFileContent = async (fileId: string): Promise<Blob> => {
    try {
      if (!accessToken) {
        throw new Error("Access token is not available.");
      }

      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch file content: ${response.statusText}`);
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      throw error;
    }
  };

  const pickerCallback = async (data: any, onFilePicked: (files: File[]) => void) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const docs = data[google.picker.Response.DOCUMENTS] || data.docs || [];

      if (!docs.length) {
        return;
      }

      try {
        const filePromises = docs.map(async (file: any) => {
          const fileContent = await fetchFileContent(file.id);
          const fileAsFileType = new File([fileContent], file.name, {
            type: file.mimeType || "application/octet-stream",
            lastModified: Date.now(),
          });

          return fileAsFileType;
        });

        const fileObjects = await Promise.all(filePromises);
        onFilePicked(fileObjects); 
      } catch (err) {
        // Silently handle file processing errors
      }
    }
  };

  useEffect(() => {
    (async () => {
      initClient()
        .then(() => {
          setPickerLoaded(true);
        })
        .catch((error) => {
          // Silently handle initialization errors
        });
    })();
  }, [initClient]);

  return (
    <GoogleDriveContext.Provider
      value={{
        accessToken,
        isSignedIn,
        initClient,
        signIn,
        signOut,
        listFiles,
        downloadFile,
        handleFileSelection,
        initTokenClient: () => { },
        handleOpenPicker,
        pickerLoaded,
        getBackendToken,
      }}
    >
      {children}
    </GoogleDriveContext.Provider>
  );
};

export const useGoogleDrive = () => {
  return useContext(GoogleDriveContext);
};
